import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Shield, Sparkles, Layers, TrendingUp } from "lucide-react";

const steps = [
  { num: "01", Icon: Shield, title: "Foundational Trust", desc: "Build the ethical framework and governance structure that earns stakeholder confidence and regulatory alignment." },
  { num: "02", Icon: Sparkles, title: "Skills & Tools", desc: "Equip your workforce with AI literacy, practical tools, and the mindset to embrace transformation." },
  { num: "03", Icon: Layers, title: "Embedded Delivery", desc: "Move from pilots to production—embedding AI into core workflows, not innovation labs." },
  { num: "04", Icon: TrendingUp, title: "Exponential Growth", desc: "Scale what works. Let compounding returns from AI-enhanced operations drive your competitive advantage." },
];

export default function ProcessSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section data-testid="process-section" ref={ref} className="py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#C4963B] font-semibold mb-4">
            The Framework
          </p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl leading-tight">
            How we scale AI—responsibly.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={s.num}
              data-testid={`process-step-${s.num}`}
              className="group relative p-7 md:p-8 bg-[#1E2229] border border-white/[0.04] hover:border-[#C4963B]/25 transition-all duration-500"
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="font-['Cormorant_Garamond'] text-7xl font-light text-white/[0.025] absolute top-3 right-4 select-none">
                {s.num}
              </span>
              <s.Icon className="w-5 h-5 text-[#C4963B] mb-6" strokeWidth={1.5} />
              <h3 className="text-base font-medium text-[#F9F6F0] mb-3">
                {s.title}
              </h3>
              <p className="text-sm text-[#E8E6E1]/40 leading-relaxed">
                {s.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
